<template>
  <VDataGridV2
    id="tenders-registry"
    :loader="dataLoader"
    entity-type="tender"
    layout="fill"
    paginated
    :skeleton-row-count="30"
    clickable
    :context-menu="TendersService.contextMenu"
    @row:dblclick="openTenderCard"
  >
    <VDataGridColumnV2
      id="isBookmarked"
    >
      <template #default="{ row }">
        <VButton
          :title="TendersService.getIsBookmarkedText(row)"
          theme="link"
          @click="TendersService.toggleCategoryBookmark(row)"
        >
          <VIcon :material="TendersService.getIsBookmarkedIcon(row)"/>
        </VButton>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('status')"
      id="status"
      label="Статус"
      content-align="left"
      :filterable="showStatusFilter"
      filter-type="select"
      :filter-props="{
        options: TendersService.STATUS_FILTER_OPTIONS,
        optionLabel: 'name'
      }"
      :content-class="(row) => ({
        'ellipsis': true,
				'in-progress': row.isRunning,
				'deleted':     row.isDeleted,
				'is-refused':  row.isRefusedTenderByCurrentPartner,
			})"
    >
      <template #default="{ row }">
        {{ row.status }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('productCode')"
      id="productCode"
      content-align="center"
      label="Арт"
      width="68px"
      :content-class="(row) => ({ 'is-refused': row.isRefusedTenderByCurrentPartner })"
    >
      <template #default="{ row }">
        {{ row.productCode }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('name')"
      id="name"
      label="Наименование лота"
      width="560px"
      :content-class="(row) => ({ 'is-refused': row.isRefusedTenderByCurrentPartner })"
      filterable
      filter-identity="pattern"
      :filter-props="{
        placeholder: 'Поиск по названию'
      }"
    >
      <template #default="{ row }">
        <div class="name-container">
					<span :title="$core.currentPartner!.isSeller ? row.productCategory.name : row.product.name" class="ellipsis">
						{{ $core.currentPartner!.isSeller ? row.productCategory.name : row.product.name }}
					</span>

          <VBadges>
            <VBadge class="default">
              {{ row.qtyDesired?.format(row.unit) }}
            </VBadge>
            <VBadge v-if="row.desiredReceiptDate" class="default">
              к {{ row.desiredReceiptDate.format() }}
            </VBadge>
            <VBadge v-if="row.preorderShipmentPeriod" class="default">
              {{ row.preorderShipmentPeriod.toString({ prefix: 'с', middle: 'по' }) }}
            </VBadge>
            <VBadge
              v-if="row.isQtyOrderedFlexible"
              class="default ellipsis badge-free-buyout"
              @mouseenter="$core.tooltip.show($event.currentTarget, 'Свободный выкуп')"
              @mouseleave="$core.tooltip.hide()"
            >
              Свободный выкуп
            </VBadge>
            <VBadge v-if="TendersService.isWinnersCategorySplit(row.winnersCategory)" class="default">
              70/30%
            </VBadge>
          </VBadges>
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('priceStartFull')"
      id="priceStartFull"
      width="160px"
      content-align="right"
      label="Стартовая цена"
      header-class="ellipsis"
      :content-class="(row) => ({ 'ellipsis': true, 'is-refused': row.isRefusedTenderByCurrentPartner })"
    >
      <template #default="{ row }">
        {{ row.priceStartFull.formatInDisplayCurrency(row.priceStartCurrencyCode, row.startAt, row.unit) }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="$core.currentPartner.isSeller && isColumnVisible('yourBid')"
      id="yourBid"
      width="160px"
      content-align="right"
      label="Ваша ставка"
      header-class="ellipsis"
      :content-class="(row) => ({ 'is-refused': row.isRefusedTenderByCurrentPartner })"
    >
      <template #default="{ row }">
        <VButton
          :ref="`yourBid-${row.id}`"
          :disabled="row.isFinished"
          theme="link"
          @dblclick.stop
          @click="event => showChangeBidPopup(event, row)"
        >
          <div
            class="ellipsis"
            :class="{
							'red':   row.yourLastBid && row.bestBid && row.yourLastBid.id !== row.bestBid.id,
							'green': row.yourLastBid && row.bestBid && row.yourLastBid.id === row.bestBid.id,
						}"
          >
            {{ row.yourLastBid?.priceFullBuyerWithDeferredPayment.formatInDisplayCurrency(row.yourLastBid.offerCurrencyCode, row.startAt, row.yourLastBid.unit) || '--' }}
          </div>
        </VButton>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('bestBid')"
      id="bestBid"
      width="160px"
      content-align="right"
      label="Последняя ставка"
      header-class="ellipsis"
      :content-class="(row) => ({ 'is-refused': row.isRefusedTenderByCurrentPartner })"
    >
      <template #default="{ row }">
        <div
          class="ellipsis"
          :class="{
						'red': row.bestBid?.priceFullBuyerWithDeferredPayment.greaterThan(row.priceStartFull),
					}"
        >
          {{ row.bestBid?.priceFullBuyerWithDeferredPayment.formatInDisplayCurrency(row.bestBid.offerCurrencyCode, row.startAt, row.bestBid.unit) || '--' }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('endAt')"
      id="endAt"
      :content-class="(row) => ({ 'ellipsis': true, 'is-refused': row.isRefusedTenderByCurrentPartner })"
      label="Время окончания"
      header-class="ellipsis"
    >
      <template #default="{ row }">
        {{ row.timeRemainingBeforeNextEvent }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="showSuppliersStats"
      id="approvedCount"
      width="110px"
      content-align="right"
      label="Одобрено"
      header-class="ellipsis"
      hint="Одобрены на товар тендера"
    >
      <template #default="{ row }">
        {{ row.suppliersStats?.approvedCount ?? '--' }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="showSuppliersStats"
      id="availableCount"
      width="110px"
      content-align="right"
      label="Доступно"
      header-class="ellipsis"
      hint="Одобрены, не исключены из торгов, сырьё доступно"
      filterable
      filter-type="select"
      :filter-props="{
        options: [
          { id: 1, name: 'Не состоится', availableSuppliersCount: 0 },
          { id: 2, name: 'Одна ставка',  availableSuppliersCount: 1 },
        ],
        clearable: true,
      }"
      filter-identity="availableSuppliersCount"
    >
      <template #default="{ row }">
        {{ row.suppliersStats?.availableCount ?? '--' }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="showSuppliersStats"
      id="withBidCount"
      width="110px"
      content-align="right"
      label="Со ставкой"
      header-class="ellipsis"
      hint="Сделали хотя бы одну ставку"
    >
      <template #default="{ row }">
        {{ row.suppliersStats?.withBidCount ?? '--' }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="showSuppliersStats"
      id="refusedCount"
      width="110px"
      content-align="right"
      label="Отказались"
      header-class="ellipsis"
      hint="Ставок нет, есть отказ"
      filterable
      filter-type="select"
      :filter-props="{
        options: [
          { id: 1, name: 'Есть', hasRefusedSuppliers: true  },
          { id: 2, name: 'Нет',  hasRefusedSuppliers: false },
        ],
        clearable: true,
      }"
      filter-identity="hasRefusedSuppliers"
    >
      <template #default="{ row }">
        {{ row.suppliersStats?.refusedCount ?? '--' }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="showSuppliersStats"
      id="withoutBidCount"
      width="110px"
      content-align="right"
      label="Без ставки"
      header-class="ellipsis"
      hint="Сырьё доступно, ставок и отказов нет"
      filterable
      filter-type="select"
      :filter-props="{
        options: [
          { id: 1, name: 'Есть', hasSuppliersWithoutBid: true  },
          { id: 2, name: 'Нет',  hasSuppliersWithoutBid: false },
        ],
        clearable: true,
      }"
      filter-identity="hasSuppliersWithoutBid"
    >
      <template #default="{ row }">
        {{ row.suppliersStats?.withoutBidCount ?? '--' }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('hasOrder')"
      id="hasOrder"
      content-align="left"
      label="Заказ"
      width="250px"
      content-class="ellipsis"
    >
      <template #default="{ row }">
        <template v-if="$core.utils.isNotEmptyArray(row.orderPositions)">
          <TenderOrdersCounter
            :tender="row"
          />
        </template>
        <template v-else>Нет</template>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('buyer')"
      id="buyer"
      label="Покупатель"
      width="150px"
      :content-class="(row) => ({ 'ellipsis': true, 'is-refused': row.isRefusedTenderByCurrentPartner })"
    >
      <template #default="{ row }">
        <div class="ellipsis" :title="row.buyer?.name">
          {{ row.buyer?.name ?? '--' }}
        </div>
      </template>
    </VDataGridColumnV2>
  </VDataGridV2>
</template>

<style lang="scss" src="./TendersRegistry.scss"/>

<script lang="ts">
import type Tender         from "@/domains/Tenders/Tender";
import TendersService      from '@/domains/Tenders/TendersService';
import DataGridLoader      from '@/lib/components/VDataGridV2/DataGridLoader';
import TenderOrdersCounter from '@/domains/Tenders/components/TenderOrdersCounter/TenderOrdersCounter.vue';

export default defineComponent({
  name: 'TendersRegistry',

  components: {
    TenderOrdersCounter,
  },

  props: {
    /**
     * Админский режим — админские колонки и isAdminMode на бэк.
     * Включается только из роутера (props: { adminMode: true }) для админского реестра.
     * При встраивании в воронки/карточки не указывается — реестр работает в регулярном режиме.
     */
    adminMode: {
      type:    Boolean,
      default: false,
    },

    /** во flow у нас табы, фильтр не нужен, для разделов нужен */

    showStatusFilter: {
      type: Boolean,
      default: true,
    },

    /**
     * id колонок, которые реестр не показывает. Решает вызывающий: раньше
     * реестр угадывал это, разбирая params (isFinished / isOrdersCreated),
     * то есть знал устройство вкладок воронки.
     * Роль зрителя остаётся ответственностью реестра — поставщику колонка
     * «Заказ» не показывается независимо от пропа.
     */
    hiddenColumns: {
      type:    Array as PropType<string[]>,
      default: () => [],
    },

    params: {
      type:    Object as PropType<Parameters<typeof TendersService.getListPaginated>[0]>,
      default: () => ({}),
    },

    /**
     * Нужен ли лонг-поллинг. Решает вызывающий: содержимое вкладок с завершёнными
     * тендерами само по себе не меняется, и опрашивать сервер по ним незачем.
     * По умолчанию true — реестр, встроенный без этого пропа, работает как раньше.
     */
    isLive: {
      type:    Boolean,
      default: true,
    },

    /** Колонки-счётчики поставщиков тендера — для вкладки «Аналитика тендеров» */
    showSuppliersStats: {
      type:    Boolean,
      default: false,
    },
  },

  setup() {
    return {
      TendersService,
    };
  },

  data() {
    return {
      dataLoader: new DataGridLoader((params) => TendersService.getListPaginated(params, {}))
        .useReactiveSource(() => {
          return {
            ...this.params,
            isAdminMode: this.adminMode === true && this.$core.currentUser?.isAdmin
          }
        })
        .onAfterLoad((result) => {
          if (result.meta?.time?.serverTime?.end) {
            this.serverTimeFetchedAt = result.meta.time.serverTime.end;
          }
        }),

      /** Мотивация - серверное время может отличаться от браузерного
       * ввиду этого, мы будем считать 5 секунд по пользовательскому времени,
       * а в пэйлоаде отправлять то, что получили в прошлый раз с сервера **/
      serverTimeFetchedAt:  null as Date | null,

      liveTimeSubscription: null as any,
      // каждые 5 секунд получаем обновления и точечно обновляем сущности
      userTimeSoftPolledAt: 0,
      // каждые 30 секунд запрашиваем полностью весь реестр
      userTimeHardPolledAt: 0,
    };
  },

  computed: {
    /**
     * Админский режим — только если включён пропсом (роутер) и зритель действительно админ.
     */
    isAdminMode(): boolean {
      return this.adminMode === true && this.$core.currentUser.isAdmin === true;
    },

    hiddenColumnsSet(): Set<string> {
      return new Set(this.hiddenColumns);
    },
  },

  created() {
    if (this.isLive === true)
    {
      this.subscribeLiveTime();
    }
    else
    {
      // Подписка не нужна
    }
  },

  beforeUnmount() {
    this.unsubscribeLiveTime();
  },


  watch: {
    /**
     * При изменении параметров (таба, фильтров) лучше обнулить время последнего пулла, тк в этот момент лоадер заново грузит данные и точка отсчета должна быть новой
     */
    params: {
      async handler() {
        this.userTimeHardPolledAt = 0;
        this.userTimeSoftPolledAt = 0;
      }
    },

    /**
     * Реестр во воронке не пересоздаётся при смене вкладки, меняются только пропсы,
     * поэтому подписку надо снимать и ставить здесь, а не в created.
     */
    isLive: {
      handler(isLive: boolean) {
        if (isLive === true)
        {
          this.subscribeLiveTime();
        }
        else
        {
          this.unsubscribeLiveTime();
        }
      }
    },
  },

  methods: {
    // ==================== Публичный API ====================

    refresh() {
      this.dataLoader.refresh();
    },

    isColumnVisible(columnId: string): boolean {
      return this.hiddenColumnsSet.has(columnId) === false;
    },

    // ==================== Polling ====================

    subscribeLiveTime() {
      if (typeof this.liveTimeSubscription === 'function')
      {
        // Уже подписаны
      }
      else
      {
        this.liveTimeSubscription = this.$core.liveTime.subscribe(this.onLiveTimeTick);
      }
    },

    unsubscribeLiveTime() {
      /* this.$core.liveTime.subscribe - возвращает функцию отписки, которую нужно вызвать */
      if (typeof this.liveTimeSubscription === 'function')
      {
        this.liveTimeSubscription();
        this.liveTimeSubscription = null;
      }
      else
      {
        // Подписки нет
      }
    },

    onLiveTimeTick(currentUserTime: number) {
      /**
       * Чтобы запрос dataLoader не перебивался
       * Было так: запрос dataLoader отправляется, происходит тик, отправляется soft pool, изменений нет, мы видим пустой даталоадер
       */
      if (this.dataLoader.isLoading) {
        this.userTimeHardPolledAt = currentUserTime;
        this.userTimeSoftPolledAt = currentUserTime;
        return;
      }

      /**
       * Hard polling проверяется первым — это даёт два эффекта:
       *   1. На одном тике никогда не сработают и hard, и soft одновременно
       *      (избегаем race condition: refresh + mergeData в произвольном порядке).
       *   2. Если ноут просыпался из спячки, между предыдущим и текущим тиком
       *      может пройти час и больше. В этом случае hard interval (30 сек)
       *      гарантированно превышен — выполнится полный refresh, а не дельта-fetch
       *      с устаревшим updatedAfter, который вернул бы кашу.
       */
      if (currentUserTime - this.userTimeHardPolledAt > TendersService.config.HARD_POLL_INTERVAL) {
        this.userTimeHardPolledAt = currentUserTime;
        this.userTimeSoftPolledAt = currentUserTime;
        this.dataLoader.refresh();
        return;
      }

      if (currentUserTime - this.userTimeSoftPolledAt > TendersService.config.SOFT_POLL_INTERVAL) {
        this.userTimeSoftPolledAt = currentUserTime;
        this.softPoll();
      }

    },

    // todo решение сомнительное, по идее это ответственность лоадера,
    //  но чет не хочется туда суваться сейчас
    // + если засовывать это в лоадер, нужно думать откуда взять serverTimeFetchedAt
    async softPoll() {
      const result = await TendersService.getListPaginated(
        {
          ...this.params,
          updatedAfter: this.serverTimeFetchedAt,
        },
        // Опрос идёт раз в 5 с (SOFT_POLL_INTERVAL): зависший запрос обрываем раньше следующего тика,
        // чтобы запросы не копились, а отмена предыдущего не даёт старому ответу перетереть новый
        {timeout: 2900, abortPrevious: true},
      );

      this.dataLoader.mergeData(result.data);

      if (result.meta?.time?.serverTime?.end) {
        this.serverTimeFetchedAt = result.meta.time.serverTime.end;
      }
    },

    // ==================== Действия ====================

    async openTenderCard(tender: Tender): Promise<void> {
      this.$core.dialogs.TenderChangeBidPopup.hideIfOpen();

      if (await TendersService.showTenderCard(tender)) {
        this.dataLoader.refresh();
      }
    },

    /** Понижение собственной ставки — попап у кнопки с ценой. */
    async showChangeBidPopup(event: MouseEvent, tender: Tender): void {
      this.$core.dialogs.TenderChangeBidPopup.hideIfOpen()
      /** Если по этому тендеру у поставщика нет ставки, то ничего не делаем */
      if (tender.yourLastBid === null) {
        return;
      }

      // Тендеры приходящий из гетлиста лишены transportRoute, приходится делать tenders.get
      const response = await TendersService.get(tender.id, null, this.$core.timeZone, false, {});
      const _tender = response?.tender;
      if (this.$core.utils.isNullOrUndefined(_tender)) return;

      const isNeedToUpdate = await this.$core.dialogs.TenderChangeBidPopup.showAsPromise(
        {
          bid:     _tender.yourLastBid,
          bestBid: _tender.bestBid,
          target:  event.target,
        }
      );
      if (isNeedToUpdate) this.dataLoader.refresh();
    },
  },
});
</script>
